import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import { HTTP_ENDPOINTS } from '../../../../clients/clients.constants';
import { setStoreDateRange, setStoreEquipmentTypeIds } from "../../../../store/analyticsSelections";
import { http } from '../../../../clients/http/http.client';

import EquipmentDropdown from '../../../Inputs/EquipmentDropdown/EquipmentDropdown';
import DateRangeSelectorV2 from "../../../Inputs/DateRangeSelectorV2/DateRangeSelector";
import Widget from '../../../WidgetItem/WidgetItem';

function formatDuration(minutes) {
  const total = Math.round(Number(minutes) || 0);
  const days = Math.floor(total / 1440);
  const rem = total % 1440;
  const hrs = Math.floor(rem / 60);
  const mins = rem % 60;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hrs) parts.push(`${hrs}h`);
  if (mins || parts.length === 0) parts.push(`${mins}m`);
  return parts.join(' ');
}

export default function AnalyticsWidgetsPanel({
                                                siteId,
                                                dateFrom,
                                                dateTo,
                                                withDateRange = false,
                                                storeOpeningHour = '08:00',
                                                title = 'Backroom to Instore Movements',
                                                maxContainerWidth = '590px',
                                                mx = 'auto',
                                              }) {
  const dispatch = useDispatch();
  const equipmentStoreTypeIds = useSelector(state => state.analyticsSelections.equipmentTypeIds);
  const [equipmentTypeIds, setEquipmentTypeIds] = useState(equipmentStoreTypeIds);
  const [equipmentOptions, setEquipmentOptions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await http.client.get(`${HTTP_ENDPOINTS.EQUIPMENT_TYPES}/`);
        setEquipmentOptions(res.equipmentTypes || []);
      } catch {
        setEquipmentOptions([]);
      }
    })();
  }, []);

  const handleEquipmentTypeChange = (ids) => {
    setEquipmentTypeIds(ids);
    dispatch(setStoreEquipmentTypeIds(ids));
  };

  const handleDateRangeChange = (range) => {
    console.log('range',range);
    if (range.length) {
      const startISO = range[0].toISOString();
      const endISO = range[1].toISOString();
      console.log('range', range);
      dispatch(setStoreDateRange({ start: startISO, end: endISO }));
    } else {
      dispatch(setStoreDateRange({ start: null, end: null }));
    }
  };

  const buildParams = () => {
    const params = new URLSearchParams();
    if (siteId != null) params.append('siteId', siteId);

    if (dateFrom && dateTo) {
      params.append('from', DateTime.fromISO(dateFrom).toUTC().toISO());
      params.append('to', DateTime.fromISO(dateTo).toUTC().toISO());
    }

    if (equipmentTypeIds && equipmentTypeIds.length) {
      params.append('equipmentTypeIds', equipmentTypeIds.join(','));
    }
    params.append('storeOpeningHour', storeOpeningHour);
    return params.toString();
  };

  const queryString = useMemo(buildParams, [siteId, dateFrom, dateTo, equipmentTypeIds, storeOpeningHour]);

  const widgetConfigs = [
    {
      title: 'Selected equipment in stock',
      endpoint: HTTP_ENDPOINTS.EQUIPMENT_WIDGETS_SELECTED_EQUIPMENT,
      value: (data) => data?.blueDolliesInStock?.toString() || '0',
      link: '/asset-tracking?view=selected-equipment'
    },
    {
      title: 'Were in use',
      endpoint: HTTP_ENDPOINTS.EQUIPMENT_WIDGETS_WERE_IN_USE,
      value: (data) => `${data?.inUseEntityCount || 0}/${data?.blueDolliesInStock || 0}`,
      subtitle: (data) => `${(data?.blueDolliesInStock || 0) - (data?.inUseEntityCount || 0)} not used`,
      link: '/asset-tracking?view=were-in-use',
      infoIcon: true,
      infoTooltip: 'Number of unique tags of the selected item type which have been in use during the selected time.'
    },
    {
      title: 'Total trips',
      endpoint: HTTP_ENDPOINTS.EQUIPMENT_WIDGETS_TOTAL_TRIPS,
      value: (data) => data?.OverallTotalTrips?.toString() || '0',
      subtitle: (data) => `${data?.OverallOpeningTrips || 0} before store opening hours`,
      link: '/asset-tracking?view=trips',
      infoIcon: true,
      infoTooltip: 'Total number of trips and count of trips before store opening hours.'
    },
    {
      title: 'Average time Instore',
      endpoint: HTTP_ENDPOINTS.EQUIPMENT_WIDGETS_AVERAGE_TIME_INSTORE,
      value: (data) => formatDuration(data?.averageTimeInStore),
      link: '/asset-tracking?view=in-transit',
      infoIcon: true,
      infoTooltip: 'Average duration of all trips during the selected time.'
    },
    {
      title: 'Longest time Instore',
      endpoint: HTTP_ENDPOINTS.EQUIPMENT_WIDGETS_LONGEST_TIME_INSTORE,
      value: (data) => formatDuration(data?.longestTimeInStore),
      link: '/asset-tracking?view=in-store',
      infoIcon: true,
      infoTooltip: 'Maximum duration among all trips.'
    },
    {
      title: 'Shortest time Instore',
      endpoint: HTTP_ENDPOINTS.EQUIPMENT_WIDGETS_SHORTEST_TIME_INSTORE,
      value: (data) => formatDuration(data?.shortestTimeInStore),
      link: '/asset-tracking?view=in-store',
      infoIcon: true,
      infoTooltip: 'Minimum duration among all trips.'
    },
    {
      title: 'In transit',
      endpoint: HTTP_ENDPOINTS.EQUIPMENT_WIDGETS_IN_TRANSIT,
      value: (data) => data?.inTransit?.toString() || '0',
      subtitle: (data) => `Avg Trip time: ${formatDuration(data?.AvgTripMinutesToFromARRC)}`,
      link: '/asset-tracking?view=in-transit',
      infoIcon: true,
      infoTooltip: 'Count of tags currently in transit during the selected time.'
    }
  ];

  return (
    <Box sx={{ maxWidth: maxContainerWidth, mx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography sx={{ fontSize: 22, color: '#1B3665' }}>{title}</Typography>
        <EquipmentDropdown
          type="equipment"
          options={equipmentOptions}
          selectedEquipment={equipmentTypeIds}
          handleSelectionChange={handleEquipmentTypeChange}
          width="200px"
        />
        {withDateRange && (
          <Box sx={{ ml: 'auto' }}>
            <DateRangeSelectorV2
              value={
                dateFrom && dateTo
                  ? [new Date(dateFrom), new Date(dateTo)]
                  : [null, null]
              }
              onChange={handleDateRangeChange}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {widgetConfigs.map((cfg, idx) => (
          <Link key={idx} to={cfg.link} style={{ textDecoration: 'none', width: '185px' }}>
            <Box sx={{ transition: 'opacity 0.3s', '&:hover': { opacity: 0.7 }}}>
              <Widget
                endpoint={cfg.endpoint}
                queryString={queryString}
                getValue={cfg.value}
                getSubtitle={cfg.subtitle}
                title={cfg.title}
                infoIcon={cfg.infoIcon}
                infoTooltip={cfg.infoTooltip}
              />
            </Box>
          </Link>
        ))}
      </Box>
    </Box>
  );
}

AnalyticsWidgetsPanel.propTypes = {
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  dateFrom: PropTypes.string,
  dateTo: PropTypes.string,
  withDateRange: PropTypes.bool,
  storeOpeningHour: PropTypes.string,
  title: PropTypes.string,
  maxContainerWidth: PropTypes.string,
  mx: PropTypes.string,
};